import { Component, ElementRef, ViewChild, effect, inject } from '@angular/core';
import { ConfirmService } from '../../../core/services/confirm.service';

/**
 * CONFIRM DIALOG
 * --------------
 * The popup behind `ConfirmService`. Built on the native `<dialog>` element,
 * so the browser supplies the modal behaviour for free: the page behind is
 * inert, focus is trapped inside, and Esc cancels.
 *
 * Focus lands on the cancel button, never on the action — pressing Enter on a
 * popup you didn't read should never delete anything.
 */
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
})
export class ConfirmDialogComponent {
  private readonly confirm = inject(ConfirmService);

  @ViewChild('dialog', { static: true }) private dialogRef!: ElementRef<HTMLDialogElement>;

  readonly request = this.confirm.pending;

  constructor() {
    effect(() => {
      const open = this.request() !== null;
      // A macrotask, not a microtask: by then the card has rendered, so there
      // is a Cancel button to take focus.
      setTimeout(() => {
        const dialog = this.dialogRef?.nativeElement;
        if (!dialog) return;
        if (open && !dialog.open) {
          dialog.showModal();
          dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
        } else if (!open && dialog.open) {
          dialog.close();
        }
      });
    });
  }

  answer(confirmed: boolean): void {
    this.confirm.answer(confirmed);
  }

  /**
   * Esc is a "no". Handled on keydown as well as on the dialog's own `cancel`
   * event, because browsers don't always fire `cancel` for it; answering twice
   * is harmless, the second answer finds nothing pending.
   */
  onCancel(event: Event): void {
    event.preventDefault();
    this.answer(false);
  }

  /** A click on the dialog element itself, not its card, is a click on the backdrop. */
  onClick(event: MouseEvent): void {
    if (event.target === this.dialogRef.nativeElement) this.answer(false);
  }
}
